import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { searchBulletins } from 'reducers/BulletinReducer';
import { displayDateTime } from 'reducers/TimeReducer';
import { handleError } from 'reducers/ErrorReducer';
import { makeStyles } from '@material-ui/core/styles';
import SearchDatePicker from '../components/SearchDatePicker2';
import { addBulletin, editBulletin } from 'reducers/DialogsReducer';
import Tooltip from 'components/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import EditIcon from '@material-ui/icons/Edit';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import { showReport } from 'reducers/DialogsReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import XGrid3 from 'components/XGrid3';
import LocalPrintshopRoundedIcon from '@material-ui/icons/LocalPrintshopRounded';
import DeleteIcon from '@material-ui/icons/Delete';
import { notify } from 'reducers/NotifierReducer';
import { removeAlert } from 'reducers/BulletinReducer';
import TextField2 from 'components/TextField2';
import RecordLimitAlert from 'Search/components/RecordLimitAlert';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';
import Checkbox2 from 'components/Checkbox2';
import Dictionary from 'components/Dictionary';
import { gridStyle } from 'utils/formStyles';
import NoPermissionRedirect from 'components/NoPermissionRedirect';
import { checkReportPermission } from 'utils/functions';

const useStyles = makeStyles((theme) => ({
  filters: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(2),
    },
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
  formControl: {
    marginLeft: theme.spacing(2),
    minWidth: 200,
    marginBottom: theme.spacing(1),
  },
  panel: {
    '& .MuiDataGridPanelFooter-root': {
      display: 'none',
    },
  },
  search: {
    width: 450,
    marginRight: 8,
  },
  statusLabel: {
    color: theme.colors.text1,
  },
  item: gridStyle(200, 200),
}));

const defaultColumns = [
  {
    field: 'AlertType',
    headerName: 'Bulletin Type',
  },
  {
    field: 'AlertTypeDescription',
    headerName: 'Bulletin Type Description',
  },

  'AgencyID',
  {
    field: 'Beginning',
    valueFormatter: (params) => displayDateTime(params.value),
  },
  {
    field: 'Ending',
    valueFormatter: (params) => displayDateTime(params.value),
  },
  'Person',
  'Address',
  'Notification',
  'Priority',
  'AlertID',
  'Created',
  'CreatedBy',
  'Updated',
  'UpdatedBy',
  'IsDeleted',
  'Deleted',
  'DeletedBy',
];

const noDeletedColumns = [
  {
    field: 'AlertType',
    headerName: 'Bulletin Type',
  },
  {
    field: 'AlertTypeDescription',
    headerName: 'Bulletin Type Description',
  },
  'AgencyID',
  {
    field: 'Beginning',
    valueFormatter: (params) => displayDateTime(params.value),
  },
  {
    field: 'Ending',
    valueFormatter: (params) => displayDateTime(params.value),
  },
  'Person',
  'Address',
  'Notification',
  'Priority',
  'AlertID',
  'Created',
  'CreatedBy',
  'Updated',
  'UpdatedBy',
];

function BulletinSearchResults(props) {
  const classes = useStyles();
  const permissions = getFullPermissions('cad', 'Alerts', 'any');
  const userColumns = permissions.ViewDeleted ? defaultColumns : noDeletedColumns;
  const { filter, dataUpdate } = props;
  const [rows, setRows] = useState([]);
  const [dateFrom, setDateFrom] = useState(new Date());
  const [dateTo, setDateTo] = useState(new Date());
  const [loaded, setLoaded] = useState(true);
  const [selection, setSelection] = useState(null);
  const [columns, setColumns] = useState(userColumns);
  const [printFlag, setPrintFlag] = useState(false);
  const [text, setText] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const limit = 1000;
  const [page, setPage] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  //const [viewDeleted, setViewDeleted] = useState(false);
  const [status, setStatus] = useState('all');
  const [read, setRead] = useState('all');
  const [alertType, setAlertType] = useState({ Code: null, Description: null });
  useEffect(() => {
    search();
  }, [filter, status, read]);

  useEffect(() => {
    if (!dataUpdate || dataUpdate.type !== 'Bulletin') return;
    search();
  }, [dataUpdate]);

  useEffect(() => {
    if (rows.length === rowCount) setShowAlert(false);
    else if (rows.length >= limit) setShowAlert(true);
  }, [rows, rowCount]);

  const processData = (data, nextStartingIndex = 0) => {
    return data.map((row, idx) => {
      return { ...row, id: idx + nextStartingIndex, Read: row.ReadDate ? true : false };
    });
  };

  const handleShowAll = async () => {
    if (rows.length >= rowCount) return;

    let offset = rows.length;
    let remaining = rowCount - rows.length;

    while (remaining > 0) {
      const safeLimit = Math.min(limit, remaining);
      await search(offset, true, safeLimit);
      offset += safeLimit;
      remaining = rowCount - offset;
    }
  };

  const search = async (offset = 0, mergeRows = false, customLimit = limit) => {
    setLoaded(false);

    try {
      const result = await searchBulletins(
        text,
        dateFrom,
        dateTo,
        filter,
        status,
        read,
        alertType?.Code,
        offset,
        customLimit
      );

      result.length ? setRowCount(result[0].Count) : setRowCount(0);

      if (mergeRows) {
        setRows((prevState) => {
          const nextStartingIndex = prevState.length;
          return [...prevState, ...processData(result, nextStartingIndex)];
        });
      } else {
        setRows(processData(result));
        setPage(0);
      }

      return result;
    } catch (err) {
      props.handleError(err);
      return [];
    } finally {
      setLoaded(true);
    }
  };

  const viewDetails = async () => {
    const item = rows.find((row) => row.id === selection);
    if (!item || !checkReportPermission(26)) return;
    props.showReport({ id: 26, data: { ptsAlertID: item.ptsAlertID }, title: 'Bulletin Details' });
  };

  const add = () => props.addBulletin();

  const edit = () => {
    const item = rows.find((row) => row.id === selection);
    if (!item) return;
    props.editBulletin(item.ptsAlertID);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete selected bulletin?')) return;
    try {
      const item = rows.find((row) => row.id === selection);
      if (!item) return;
      await removeAlert(item.ptsAlertID);
      search();
      //props.notify('Bulletin removed', 'success');
    } catch (err) {
      props.handleError(err, 'Error, Unable to remove bulletin');
    }
  };

  const handlePrintClick = () => {
    setPrintFlag(true);
    setTimeout(() => setPrintFlag(false), 1000);
  };

  const onPageChange = () => {
    const offset = (page + 1) * limit;
    const mergingRows = true;
    search(offset, mergingRows);
    setPage(page + 1);
  };
  if (!permissions?.Read) return <NoPermissionRedirect />;
  const renderActions = () => {
    return (
      <div className={classes.actions}>
        <Tooltip title="Print list">
          <Fab size="small" color="secondary" onClick={handlePrintClick}>
            <LocalPrintshopRoundedIcon />
          </Fab>
        </Tooltip>
        {permissions.Create && (
          <Tooltip title="Create bulletin">
            <Fab onClick={add} size="small" color="secondary">
              <AddIcon />
            </Fab>
          </Tooltip>
        )}
        {selection != null && (
          <>
            <Tooltip title="Edit Bulletin">
              <Fab onClick={edit} size="small" color="secondary">
                <EditIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Delete Bulletin">
              <Fab onClick={handleDelete} size="small" color="secondary">
                <DeleteIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="View details">
              <Fab
                className="ml-2"
                size="small"
                color="secondary"
                disabled={!checkReportPermission(26)}
                onClick={viewDetails}>
                <InfoIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </div>
    );
  };

  const renderSearch = () => {
    const onChange = (ev, val) => setText(val);
    const onKeyDown = (ev) => ev.key === 'Enter' && search();
    return (
      <TextField2
        value={text}
        onChange={onChange}
        type="search"
        className={classes.search}
        onKeyDown={onKeyDown}
        compact="true"
      />
    );
  };

  return (
    <div>
      <h5>Bulletin History</h5>
      <Grid container xs={12}>
        <Grid container item xs={12} justify="flex-start" alignItems="center">
          {renderSearch()}
          {/* {permissions.ViewDeleted && (
            <Checkbox2
              label="Show Deleted"
              checked={viewDeleted}
              onChange={(ev, val) => setViewDeleted(val)}
            />
          )} */}
          <div style={{ marginTop: '10px' }}>
            <Dictionary
              options="AlertTypes"
              className={classes.item}
              onChange={(ev, val) => setAlertType(val)}
              value={alertType}
              label="Alert Type"
              compact="true"
            />
          </div>
          <div style={{ marginLeft: '15px' }}>
            <FormLabel id="radio-label" className={classes.statusLabel}>
              Read Filter
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="radio-label"
              name="row-radio-buttons-group-two"
              value={read}
              onChange={(event) => setRead(event.target.value)}>
              <FormControlLabel value={'all'} control={<Radio />} label={'All'} />
              <FormControlLabel value={'read'} control={<Radio />} label={'Read'} />
              <FormControlLabel value={'unread'} control={<Radio />} label={'Unread'} />
              <FormControlLabel
                disabled={!permissions.ViewDeleted}
                value={'deleted'}
                control={<Radio />}
                label={'Deleted'}
              />
            </RadioGroup>
          </div>
          <div style={{ marginLeft: '15px' }}>
            <FormLabel id="radio-label" className={classes.statusLabel}>
              Status Filter
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="radio-label"
              name="row-radio-buttons-group"
              value={status}
              onChange={(event) => setStatus(event.target.value)}>
              <FormControlLabel value={'all'} control={<Radio />} label={'All'} />
              <FormControlLabel value={'active'} control={<Radio />} label={'Active'} />
              <FormControlLabel value={'inactive'} control={<Radio />} label={'Inactive'} />
            </RadioGroup>
          </div>
        </Grid>

        <Grid item xs={12}>
          <SearchDatePicker
            search={search}
            dateFrom={dateFrom}
            dateTo={dateTo}
            setDateFrom={setDateFrom}
            setDateTo={setDateTo}
            actions={renderActions()}
            loaded={loaded}
          />
        </Grid>
      </Grid>

      {showAlert && (
        <RecordLimitAlert
          handleNextPage={onPageChange}
          handleShowAll={handleShowAll}
          rowCount={rowCount}
          currentRows={rows.length}
        />
      )}
      <XGrid3
        name="Bulletin History"
        columns={columns}
        setColumns={setColumns}
        rows={rows}
        loading={!loaded}
        setSelection={setSelection}
        printFlag={printFlag}
        viewDetails={viewDetails}
        refresh={search}
        // pagination={true}
        // pageSize={limit}
        // onPageChange={onPageChange}
        // paginationMode="server"
        // rowCount={rowCount}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    addState: state.dialogs.addBulletin,
    bulletinDetails: state.dialogs.bulletinDetails,
    unreadBulletins: state.unreadBulletins,
    dataUpdate: state.dataUpdate,
  };
};

export default connect(mapStateToProps, {
  handleError,
  addBulletin,
  editBulletin,
  showReport,
  notify,
})(BulletinSearchResults);
